import { createSlice, PayloadAction } from '@reduxjs/toolkit'

export interface ProductSize {
  id: number
  name: string
  price: number
  isSelected?: boolean
}

export interface ProductAdditional {
  id: number
  name: string
  price: number
  isSelected?: boolean
  groupId?: number
  groupName?: string
}

export interface ProductDetails {
  id: number
  name: string
  name_en?: string
  description?: string
  description_en?: string
  image?: string
  basePrice: number
  calories?: string
  sizes: ProductSize[]
  additionals: ProductAdditional[]
  additionalsGroups: Array<{
    id: number
    name: string
    name_en?: string
    items: ProductAdditional[]
  }>
}

export interface ProductDetailsState {
  productData: ProductDetails | null
  isLoading: boolean
  isAddingToCart: boolean
  selectedSize: ProductSize | null
  selectedAdditionals: ProductAdditional[]
  quantity: number
  totalPrice: number
  error: string | null
}

const initialState: ProductDetailsState = {
  productData: null,
  isLoading: false,
  isAddingToCart: false,
  selectedSize: null,
  selectedAdditionals: [],
  quantity: 1,
  totalPrice: 0,
  error: null,
}

const productDetailsSlice = createSlice({
  name: 'productDetails',
  initialState,
  reducers: {
    // Initialize product details (equivalent to Flutter's initializeProductDetails)
    initializeProductDetails: (state, action: PayloadAction<Partial<ProductDetails>>) => {
      state.isLoading = true
      state.error = null
      state.productData = action.payload as ProductDetails
      state.selectedSize = null
      state.selectedAdditionals = []
      state.quantity = 1
      state.totalPrice = action.payload.basePrice || 0
    },

    // Set loading state
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload
    },

    // Set adding to cart state
    setAddingToCart: (state, action: PayloadAction<boolean>) => {
      state.isAddingToCart = action.payload
    },

    // Select size (equivalent to Flutter's selectSize)
    selectSize: (state, action: PayloadAction<ProductSize>) => {
      state.selectedSize = action.payload
      state.totalPrice = calculateTotalPrice(state)
    },

    // Toggle additional (equivalent to Flutter's toggleAdditional)
    toggleAdditional: (state, action: PayloadAction<ProductAdditional>) => {
      const additional = action.payload
      const existingIndex = state.selectedAdditionals.findIndex(
        item => item.id === additional.id && item.groupId === additional.groupId
      )

      if (existingIndex >= 0) {
        // Remove if already selected
        state.selectedAdditionals.splice(existingIndex, 1)
      } else {
        // Add if not selected
        state.selectedAdditionals.push(additional)
      }

      state.totalPrice = calculateTotalPrice(state)
    },

    // Update quantity (equivalent to Flutter's updateQuantity)
    updateQuantity: (state, action: PayloadAction<number>) => {
      state.quantity = Math.max(1, action.payload)
      state.totalPrice = calculateTotalPrice(state)
    },

    // Increment quantity
    incrementQuantity: (state) => {
      state.quantity += 1
      state.totalPrice = calculateTotalPrice(state)
    },

    // Decrement quantity
    decrementQuantity: (state) => {
      state.quantity = Math.max(1, state.quantity - 1)
      state.totalPrice = calculateTotalPrice(state)
    },

    // Quick reset selections (equivalent to Flutter's quickResetSelections)
    quickResetSelections: (state) => {
      state.selectedSize = null
      state.selectedAdditionals = []
      state.quantity = 1
      state.totalPrice = state.productData?.basePrice || 0
    },

    // Close product dialog
    closeProductDialog: (state) => {
      state.productData = null
      state.selectedSize = null
      state.selectedAdditionals = []
      state.quantity = 1
      state.totalPrice = 0
      state.error = null
      state.isAddingToCart = false
    },

    // Set error
    setError: (state, action: PayloadAction<string>) => {
      state.error = action.payload
    },

    // Clear error
    clearError: (state) => {
      state.error = null
    },
  },
})

// Helper function to calculate total price (equivalent to Flutter's calculateTotalPrice)
function calculateTotalPrice(state: ProductDetailsState): number {
  let total = state.productData?.basePrice || 0

  // Add size price if selected
  if (state.selectedSize) {
    total = state.selectedSize.price
  }

  // Add additionals prices
  state.selectedAdditionals.forEach(additional => {
    total += additional.price
  })

  // Multiply by quantity
  return total * state.quantity
}

export const {
  initializeProductDetails,
  setLoading,
  setAddingToCart,
  selectSize,
  toggleAdditional,
  updateQuantity,
  incrementQuantity,
  decrementQuantity,
  quickResetSelections,
  closeProductDialog,
  setError,
  clearError,
} = productDetailsSlice.actions

export default productDetailsSlice.reducer
